import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import { Upload, Download, Trash2, FileText, Eye, RefreshCw, Sparkles, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { format } from 'date-fns';

const DOCUMENT_TYPES = [
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'credit_card_statement', label: 'Credit Card Statement' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'rent_agreement', label: 'Rent Agreement' },
  { value: 'utility_bill', label: 'Utility Bill' },
  { value: 'insurance', label: 'Insurance Document' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'tax_document', label: 'Tax Document' },
  { value: 'other', label: 'Other' },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function FinanceDocuments() {
  const { api } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  
  // Filters - use "all" instead of empty string for Select compatibility
  const [filterType, setFilterType] = useState('all');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());
  const [filterMonth, setFilterMonth] = useState('all');
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    notes: '',
    file: null
  });

  // CSV Upload form
  const [csvForm, setCSVForm] = useState({
    document_type: 'bank_statement',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    date_column: 'Date',
    description_column: 'Description',
    debit_column: '',
    credit_column: '',
    amount_column: 'Amount',
    file: null
  });

  useEffect(() => {
    fetchDocuments();
  }, [filterType, filterYear, filterMonth]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType && filterType !== 'all') params.append('document_type', filterType);
      if (filterYear) params.append('year', filterYear);
      if (filterMonth && filterMonth !== 'all') params.append('month', filterMonth);
      
      const res = await api.get(`/finance/documents?${params.toString()}`);
      setDocuments(res.data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.document_type) {
      toast.error('Please select a file and document type');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('document_type', uploadForm.document_type);
      formData.append('month', uploadForm.month.toString());
      formData.append('year', uploadForm.year.toString());
      formData.append('notes', uploadForm.notes || '');
      
      await api.post('/finance/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded successfully');
      setShowUpload(false);
      setUploadForm({
        document_type: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        notes: '',
        file: null
      });
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleCSVUpload = async (e) => {
    e.preventDefault();
    
    if (!csvForm.file) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvForm.file);
      formData.append('document_type', csvForm.document_type);
      formData.append('month', csvForm.month.toString());
      formData.append('year', csvForm.year.toString());
      formData.append('date_column', csvForm.date_column);
      formData.append('description_column', csvForm.description_column);
      if (csvForm.debit_column) formData.append('debit_column', csvForm.debit_column);
      if (csvForm.credit_column) formData.append('credit_column', csvForm.credit_column);
      if (csvForm.amount_column) formData.append('amount_column', csvForm.amount_column);
      
      const res = await api.post('/finance/documents/upload-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Imported ${res.data.transactions_imported} transactions from CSV`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rows had errors`);
      }
      setShowCSVUpload(false);
      setCSVForm({
        document_type: 'bank_statement',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        date_column: 'Date',
        description_column: 'Description',
        debit_column: '',
        credit_column: '',
        amount_column: 'Amount',
        file: null
      });
      fetchDocuments();
    } catch (error) {
      console.error('CSV upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/finance/documents/${doc.id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (doc) => {
    if (!confirm(`Delete "${doc.filename}"? This will also delete associated transactions.`)) return;
    
    try {
      await api.delete(`/finance/documents/${doc.id}`);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      bank_statement: 'bg-blue-100 text-blue-700',
      credit_card_statement: 'bg-purple-100 text-purple-700',
      invoice: 'bg-emerald-100 text-emerald-700',
      rent_agreement: 'bg-amber-100 text-amber-700',
      utility_bill: 'bg-orange-100 text-orange-700',
      insurance: 'bg-cyan-100 text-cyan-700',
      receipt: 'bg-slate-100 text-slate-700',
      tax_document: 'bg-red-100 text-red-700',
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      uploaded: { color: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Uploaded' },
      parsing: { color: 'bg-amber-100 text-amber-700', icon: RefreshCw, label: 'Parsing...' },
      parsed: { color: 'bg-blue-100 text-blue-700', icon: CheckCircle, label: 'Parsed' },
      needs_review: { color: 'bg-orange-100 text-orange-700', icon: AlertCircle, label: 'Needs Review' },
      posted_to_ledger: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Posted' },
      failed_parsing: { color: 'bg-red-100 text-red-700', icon: AlertCircle, label: 'Parse Failed' },
    };
    const config = statusConfig[status] || statusConfig.uploaded;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const handleParse = async (doc) => {
    if (!['bank_statement', 'credit_card_statement'].includes(doc.document_type)) {
      toast.error('Only bank and credit card statements can be parsed');
      return;
    }
    
    toast.info('Starting statement parsing... This may take a moment.');
    
    try {
      const res = await api.post(`/finance/documents/${doc.id}/parse`);
      
      if (res.data.success) {
        toast.success(`Parsed ${res.data.transactions_found} transactions!`);
        if (res.data.warnings?.length > 0) {
          toast.warning(`${res.data.warnings.length} warnings during parsing`);
        }
      } else if (res.data.needs_manual_review || res.data.raw_rows_available > 0) {
        toast.warning(`Could not parse automatically. ${res.data.raw_rows_available} raw rows available for review.`);
      } else {
        // Show specific error message
        const errorMsg = res.data.message || res.data.errors?.[0] || 'Parse failed';
        toast.error(errorMsg);
        
        // Show diagnostics in console for debugging
        if (res.data.diagnostics) {
          console.log('Parse diagnostics:', res.data.diagnostics);
        }
      }
      fetchDocuments();
    } catch (error) {
      console.error('Parse error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = error.response?.data?.message;
      toast.error(errorMessage || errorDetail || 'Failed to parse document');
    }
  };

  const handleViewDebug = async (doc) => {
    try {
      const res = await api.get(`/finance/documents/${doc.id}/debug`);
      setDebugInfo(res.data);
      setShowDebugDialog(true);
    } catch (error) {
      toast.error('Failed to fetch debug info');
    }
  };

  const canParse = (doc) => {
    return ['bank_statement', 'credit_card_statement'].includes(doc.document_type) &&
           !['parsing', 'posted_to_ledger'].includes(doc.status);
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-4" data-testid="finance-documents">
      {/* Header with Filters and Upload Button */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {DOCUMENT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterYear} onValueChange={setFilterYear}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterMonth} onValueChange={setFilterMonth}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={showCSVUpload} onOpenChange={setShowCSVUpload}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-csv-btn">
                <FileText className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Import CSV Transactions</DialogTitle>
                <DialogDescription>
                  Import transactions from a CSV file exported from your bank or credit card.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCSVUpload} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Statement Period</Label>
                    <div className="flex gap-2 mt-1">
                      <Select 
                        value={csvForm.month.toString()} 
                        onValueChange={(v) => setCSVForm({ ...csvForm, month: parseInt(v) })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map(m => (
                            <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        type="number"
                        value={csvForm.year}
                        onChange={(e) => setCSVForm({ ...csvForm, year: parseInt(e.target.value) })}
                        className="w-24"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Document Type</Label>
                    <Select 
                      value={csvForm.document_type} 
                      onValueChange={(v) => setCSVForm({ ...csvForm, document_type: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bank_statement">Bank Statement</SelectItem>
                        <SelectItem value="credit_card_statement">Credit Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg space-y-3">
                  <Label className="text-sm font-medium">Column Mapping</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Date Column *</Label>
                      <Input 
                        value={csvForm.date_column}
                        onChange={(e) => setCSVForm({ ...csvForm, date_column: e.target.value })}
                        placeholder="Date"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Description Column *</Label>
                      <Input 
                        value={csvForm.description_column}
                        onChange={(e) => setCSVForm({ ...csvForm, description_column: e.target.value })}
                        placeholder="Description"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Debit Column</Label>
                      <Input 
                        value={csvForm.debit_column}
                        onChange={(e) => setCSVForm({ ...csvForm, debit_column: e.target.value })}
                        placeholder="Debit or Withdrawal"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Credit Column</Label>
                      <Input 
                        value={csvForm.credit_column}
                        onChange={(e) => setCSVForm({ ...csvForm, credit_column: e.target.value })}
                        placeholder="Credit or Deposit"
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Or Single Amount Column</Label>
                      <Input 
                        value={csvForm.amount_column}
                        onChange={(e) => setCSVForm({ ...csvForm, amount_column: e.target.value })}
                        placeholder="Amount (negative = debit)"
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <Label>CSV File *</Label>
                  <Input 
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCSVForm({ ...csvForm, file: e.target.files[0] })}
                    className="mt-1"
                  />
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowCSVUpload(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={uploading}>
                    {uploading ? 'Importing...' : 'Import CSV'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button data-testid="upload-document-btn">
                <Upload className="h-4 w-4 mr-2" />
                Upload PDF
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Finance Document</DialogTitle>
              <DialogDescription>
                Upload bank statements, invoices, or other financial documents.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <Label htmlFor="document_type">Document Type *</Label>
                <Select 
                  value={uploadForm.document_type} 
                  onValueChange={(v) => setUploadForm({ ...uploadForm, document_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="month">Month *</Label>
                  <Select 
                    value={uploadForm.month.toString()} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, month: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => (
                        <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="year">Year *</Label>
                  <Select 
                    value={uploadForm.year.toString()} 
                    onValueChange={(v) => setUploadForm({ ...uploadForm, year: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label htmlFor="file">File *</Label>
                <Input 
                  id="file"
                  type="file"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                  accept=".pdf,.jpg,.jpeg,.png,.csv,.xlsx,.xls"
                />
                <p className="text-xs text-slate-500 mt-1">Accepts PDF, images, or spreadsheets (max 50MB)</p>
              </div>
              
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Optional notes about this document..."
                  value={uploadForm.notes}
                  onChange={(e) => setUploadForm({ ...uploadForm, notes: e.target.value })}
                />
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Documents Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No documents found</p>
          <p className="text-sm text-slate-400">Upload financial documents to get started</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Document</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Transactions</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="font-medium">{doc.filename}</p>
                        {doc.notes && <p className="text-xs text-slate-500 truncate max-w-[200px]">{doc.notes}</p>}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getTypeBadgeColor(doc.document_type)}>
                      {doc.document_type.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {MONTHS.find(m => m.value === doc.month)?.label} {doc.year}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(doc.status)}
                  </TableCell>
                  <TableCell>
                    {doc.transactions_extracted > 0 ? (
                      <Badge variant="outline">{doc.transactions_extracted} extracted</Badge>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canParse(doc) && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleParse(doc)}
                          title="Parse statement"
                          className="text-blue-500 hover:text-blue-700"
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                      )}
                      {['failed_parsing', 'needs_review'].includes(doc.status) && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleViewDebug(doc)}
                          title="View debug info"
                          className="text-amber-500 hover:text-amber-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(doc)}
                        title="Delete"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Debug Info Dialog */}
      <Dialog open={showDebugDialog} onOpenChange={setShowDebugDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Parse Debug Information</DialogTitle>
            <DialogDescription>
              Diagnostic details for document: {debugInfo?.filename}
            </DialogDescription>
          </DialogHeader>
          {debugInfo && (
            <div className="space-y-4 text-sm">
              {/* Status Overview */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">Status</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>Status: <Badge variant="outline">{debugInfo.status}</Badge></div>
                  <div>Document Type: {debugInfo.document_type}</div>
                </div>
              </div>

              {/* Parse Diagnostics */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <h4 className="font-medium mb-2">Parse Diagnostics</h4>
                <div className="space-y-1">
                  <p><strong>Extraction Method:</strong> {debugInfo.parse_diagnostics?.extraction_method}</p>
                  <p><strong>PDF Type:</strong> {debugInfo.parse_diagnostics?.pdf_type}</p>
                  <p><strong>Detected Bank:</strong> {debugInfo.parse_diagnostics?.detected_bank || 'Unknown'}</p>
                  <p><strong>Extracted Text Length:</strong> {debugInfo.parse_diagnostics?.extracted_text_length} chars</p>
                  <p><strong>Password Protected:</strong> {debugInfo.parse_diagnostics?.is_password_protected ? 'Yes' : 'No'}</p>
                  <p><strong>Candidate Rows Found:</strong> {debugInfo.parse_diagnostics?.candidate_rows_found}</p>
                  <p><strong>Transactions Created:</strong> {debugInfo.parse_diagnostics?.transactions_created}</p>
                  {debugInfo.parse_diagnostics?.llm_call_success !== null && (
                    <p><strong>LLM Call:</strong> {debugInfo.parse_diagnostics?.llm_call_success ? 'Success' : 'Failed'}</p>
                  )}
                  {debugInfo.parse_diagnostics?.llm_error && (
                    <p className="text-red-600"><strong>LLM Error:</strong> {debugInfo.parse_diagnostics.llm_error}</p>
                  )}
                  {debugInfo.parse_diagnostics?.error_reason && (
                    <p className="text-red-600"><strong>Error Reason:</strong> {debugInfo.parse_diagnostics.error_reason}</p>
                  )}
                </div>
              </div>

              {/* Raw Text Preview */}
              {debugInfo.parse_diagnostics?.raw_text_preview && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <h4 className="font-medium mb-2">Extracted Text Preview</h4>
                  <pre className="text-xs whitespace-pre-wrap bg-white p-2 rounded border max-h-32 overflow-y-auto">
                    {debugInfo.parse_diagnostics.raw_text_preview}
                  </pre>
                </div>
              )}

              {/* Raw Rows Sample */}
              {debugInfo.raw_extracted_rows_count > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <h4 className="font-medium mb-2">Raw Extracted Rows ({debugInfo.raw_extracted_rows_count} total)</h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {debugInfo.raw_extracted_rows_sample?.map((row, i) => (
                      <div key={i} className="text-xs bg-white p-1 rounded border">
                        {row.raw_line || JSON.stringify(row)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors and Warnings */}
              {debugInfo.parse_errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-red-700">Parse Errors</h4>
                  <ul className="list-disc list-inside text-red-600">
                    {debugInfo.parse_errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {debugInfo.parse_warnings?.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <h4 className="font-medium mb-2 text-amber-700">Warnings</h4>
                  <ul className="list-disc list-inside text-amber-600">
                    {debugInfo.parse_warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
